import { useEffect, useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, UserPlus, Trash2, Users, ImagePlus, Copy, Link2, CreditCard, ReceiptText, Eye, AlertTriangle } from "lucide-react";

// Readable text color for a chosen accent (dark text on light accents).
function previewText(hex) {
  if (!hex) return "#ffffff";
  const h = hex.replace("#", "");
  const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(f.slice(0, 2), 16), g = parseInt(f.slice(2, 4), 16), b = parseInt(f.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return "#ffffff";
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? "#0f172a" : "#ffffff";
}

const BILLING_BADGE = {
  owner: { label: "Owner", cls: "bg-primary text-primary-foreground border-black" },
  billed: { label: "Billed", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  pending_payment: { label: "Pending payment", cls: "bg-red-100 text-red-800 border-red-300" },
  included: { label: "Included", cls: "bg-secondary text-muted-foreground border-border" },
};


export default function FirmSeats() {
  const [firm, setFirm] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [accentColor, setAccentColor] = useState("#0f172a");
  const [billing, setBilling] = useState(null);
  const [seats, setSeats] = useState([]);
  const [seatSummary, setSeatSummary] = useState({ billed_seats: 0, monthly_cost: 0 });
  const [cardBusy, setCardBusy] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const logoInput = useRef(null);

  const load = () => api.get("/firm").then(({ data }) => { setFirm(data); setFirmName(data?.firm_name || ""); setAccentColor(data?.accent_color || "#0f172a"); }).catch(() => {});
  const loadBilling = () => api.get("/firm/billing").then(({ data }) => setBilling(data)).catch(() => {});
  const loadSeats = () => api.get("/firm/seats").then(({ data }) => { setSeats(data?.seats || []); setSeatSummary({ billed_seats: data?.billed_seats || 0, monthly_cost: data?.monthly_cost || 0 }); }).catch(() => {});
  useEffect(() => { load(); loadBilling(); loadSeats(); }, []);

  // Return from the hosted card page → finalize the default payment method.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("card_setup") === "success" && p.get("session_id")) {
      api.post(`/firm/billing/setup-complete?session_id=${p.get("session_id")}`)
        .then(() => { toast.success("Card saved and set as default"); loadBilling(); load(); })
        .catch((e) => toast.error(e.response?.data?.detail || "Could not finish card setup"));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (p.get("card_setup") === "cancel") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const addCard = async () => {
    setCardBusy(true);
    try {
      const { data } = await api.post("/firm/billing/setup-session", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
      // Safety net: if the browser blocks the redirect, don't leave the button stuck.
      setTimeout(() => setCardBusy(false), 4000);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start card setup"); setCardBusy(false); }
  };

  const retryCharge = async () => {
    setRetryBusy(true);
    try {
      const { data } = await api.post("/firm/billing/retry");
      if (data.paid) toast.success("Payment successful — seats restored");
      else toast.error(data.detail || "Charge could not be completed. Try updating your card.");
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
    setRetryBusy(false);
    loadBilling(); loadSeats(); load();
  };

  const graceDaysLeft = () => {
    if (!billing?.seat_grace_until) return null;
    const ms = new Date(billing.seat_grace_until).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  };


  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image (PNG or JPG)"); return; }
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Logo is too large (max 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => saveBranding({ logo: reader.result });
    reader.readAsDataURL(file);
  };
  const saveBranding = async (payload) => {
    setLogoBusy(true);
    try {
      await api.post("/firm/branding", payload);
      toast.success("Firm branding saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save branding"); }
    setLogoBusy(false);
  };
  const display = firm?.brand_display || (firm?.logo ? "both" : "name");
  const loginUrl = firm?.slug ? `${window.location.origin}/firm/${firm.slug}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(loginUrl); toast.success("Login link copied"); }
    catch { toast.error("Could not copy — select and copy manually"); }
  };

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter an email to invite"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/firm/invite", { email: email.trim() });
      toast.success(data.status === "added" ? `${data.email} added to your firm` : `Invite created for ${data.email}`);
      setEmail(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
    setBusy(false);
  };
  const removeMember = async (id) => {
    try {
      const { data } = await api.delete(`/firm/members/${id}`);
      toast.success(data?.seat_reclaimed ? "Member removed — billed seat credited on next invoice" : "Member removed & seat freed");
    } catch { toast.error("Could not remove member"); }
    load(); loadSeats(); loadBilling();
  };
  const cancelInvite = async (id) => { await api.delete(`/firm/invites/${id}`); load(); };
  const buySeats = async () => {
    try {
      const { data } = await api.post("/firm/seats/checkout", { quantity: 1, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start seat checkout"); }
  };

  if (!firm) return null;
  const isMember = !firm.is_owner;

  return (
    <div className="border-2 border-black p-6" data-testid="firm-seats">
      <div className="flex items-center gap-2 mb-1">
        <Building2 size={18} className="text-accent" />
        <p className="overline text-accent">Firm & seats</p>
      </div>

      {isMember ? (
        <p className="text-sm text-muted-foreground mt-2" data-testid="firm-member-note">
          You're a member of a firm — you share your firm's Pro access and case workspaces.
        </p>
      ) : (
        <>
          <div className="mb-6 pb-6 border-b border-border" data-testid="firm-branding">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><ImagePlus size={13} /> Firm logo & branded login</p>
            <p className="text-sm text-muted-foreground mb-3">Upload your firm's logo and share a private login link with your staff. Your logo is centered on that login page.</p>
            <div className="flex items-center gap-4">
              <div className="w-28 h-16 border-2 border-black bg-white grid place-items-center overflow-hidden shrink-0">
                {firm.logo
                  ? <img src={firm.logo} alt="Firm logo" data-testid="firm-logo-preview" className="max-h-full max-w-full object-contain" />
                  : <span className="text-[0.7rem] text-muted-foreground">No logo</span>}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={logoInput} type="file" accept="image/*" onChange={onLogo} data-testid="firm-logo-input" className="hidden" />
                <Button onClick={() => logoInput.current?.click()} disabled={logoBusy} size="sm" variant="outline" data-testid="firm-logo-upload-btn" className="rounded-sm gap-2 h-9">
                  <ImagePlus size={14} /> {logoBusy ? "Saving…" : firm.logo ? "Replace logo" : "Upload logo"}
                </Button>
                {!firm.slug && (
                  <Button onClick={() => saveBranding({})} disabled={logoBusy} size="sm" variant="ghost" data-testid="firm-generate-link-btn" className="rounded-sm gap-2 h-8 text-xs justify-start">
                    <Link2 size={13} /> Generate login link
                  </Button>
                )}
              </div>
            </div>
            {/* Firm display name */}
            <div className="mt-4">
              <label className="text-xs font-medium text-muted-foreground">Firm display name (shown on the login page)</label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={firmName} onChange={(e) => setFirmName(e.target.value)} placeholder="Acme Law, PLLC"
                  data-testid="firm-name-input" className="rounded-sm h-9 text-sm" />
                <Button onClick={() => saveBranding({ firm_name: firmName })} disabled={logoBusy}
                  size="sm" variant="outline" data-testid="firm-name-save-btn" className="rounded-sm h-9 shrink-0 text-xs">Save</Button>
              </div>
            </div>
            {/* What to show on the sign-in page */}
            <div className="mt-4">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Show on the sign-in page</p>
              <div className="flex gap-2" data-testid="brand-display-choice">
                {[["logo", "Logo"], ["name", "Firm name"], ["both", "Both"]].map(([val, lbl]) => {
                  const needsLogo = (val === "logo") && !firm.logo;
                  return (
                    <button key={val} type="button" onClick={() => saveBranding({ brand_display: val })} disabled={logoBusy || needsLogo}
                      title={needsLogo ? "Upload a logo first to show logo only" : undefined}
                      data-testid={`brand-display-${val}`}
                      className={`px-3 h-8 text-xs border-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${display === val ? "border-black bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Accent color */}
            <div className="mt-4">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Sign-in accent color</p>
              <div className="flex items-center gap-2">
                <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)}
                  data-testid="firm-accent-input" className="h-9 w-12 border-2 border-black cursor-pointer bg-white p-0.5" />
                <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} placeholder="#1e40af"
                  data-testid="firm-accent-hex" className="rounded-sm h-9 text-sm font-mono w-32" />
                <Button onClick={() => saveBranding({ accent_color: accentColor })} disabled={logoBusy}
                  size="sm" variant="outline" data-testid="firm-accent-save-btn" className="rounded-sm h-9 text-xs">Apply</Button>
                {firm.accent_color && (
                  <button onClick={() => { setAccentColor("#0f172a"); saveBranding({ accent_color: "" }); }}
                    data-testid="firm-accent-reset-btn" className="text-xs text-muted-foreground hover:underline">Reset</button>
                )}
              </div>
            </div>
            {/* Seat auto-grant */}
            <div className="mt-4 flex items-start gap-3">
              <button type="button" role="switch" aria-checked={firm.auto_grant_seats}
                onClick={() => saveBranding({ auto_grant_seats: !firm.auto_grant_seats })} disabled={logoBusy}
                data-testid="firm-autogrant-toggle"
                className={`mt-0.5 relative h-5 w-9 shrink-0 border-2 border-black transition-colors ${firm.auto_grant_seats ? "bg-emerald-500" : "bg-secondary"}`}>
                <span className={`absolute top-0 h-4 w-4 bg-white border border-black transition-all ${firm.auto_grant_seats ? "left-4" : "left-0"}`} />
              </button>
              <div>
                <p className="text-xs font-medium">Auto-add seats when staff join</p>
                <p className="text-[0.7rem] text-muted-foreground">When your firm is out of seats and a staff member joins via your login link, add a billed seat (${firm.seat_price}/mo) to your subscription automatically. If no card is on file the seat is granted and marked <span className="font-medium">pending payment</span>.</p>
                {firm.seat_billing_state === "pending_payment" && (
                  <p className="text-[0.7rem] text-red-600 mt-1" data-testid="seat-pending-notice">A seat is pending payment — add a payment method to keep it active.</p>
                )}
              </div>
            </div>
            {loginUrl && (
              <div className="flex items-center gap-2 mt-3" data-testid="firm-login-link-row">
                <Input readOnly value={loginUrl} data-testid="firm-login-url" className="rounded-sm h-9 text-xs font-mono" onFocus={(e) => e.target.select()} />
                <Button onClick={copyLink} size="sm" variant="outline" data-testid="firm-copy-link-btn" className="rounded-sm gap-1 h-9 shrink-0 text-xs">
                  <Copy size={13} /> Copy
                </Button>
              </div>
            )}
            {/* Live sign-in preview */}
            <div className="mt-5" data-testid="firm-brand-preview">
              <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5"><Eye size={13} /> Sign-in page preview</p>
              <div className="border-2 border-black bg-secondary/40 p-6">
                <div className="max-w-[240px] mx-auto text-center">
                  {(display !== "name") && firm.logo && (
                    <img src={firm.logo} alt="preview logo" className="max-h-14 max-w-[160px] object-contain mx-auto mb-3" />
                  )}
                  {(display === "name" || display === "both" || !firm.logo) && (
                    <div className="font-serif text-2xl text-primary mb-1" data-testid="preview-name">{firmName || "Your Firm"}</div>
                  )}
                  <p className="text-[0.7rem] text-muted-foreground mb-4">Sign in to your firm workspace</p>
                  <div className="bg-white border-2 border-black p-3">
                    <div className="h-7 border border-border mb-2 bg-secondary/50" />
                    <div className="h-7 border border-border mb-3 bg-secondary/50" />
                    <div className="h-8 grid place-items-center text-xs font-medium"
                      style={{ backgroundColor: accentColor, color: previewText(accentColor) }} data-testid="preview-button">
                      Log in
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Billing: card on file + receipts */}
          <div className="mb-6 pb-6 border-b border-border" data-testid="firm-billing">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><CreditCard size={13} /> Payment method & receipts</p>
            {billing?.seat_billing_state === "pending_payment" && (
              <div className="mb-3 p-3 border-2 border-red-300 bg-red-50" data-testid="grace-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800" data-testid="grace-countdown">
                      {graceDaysLeft() > 0
                        ? `A seat payment failed — ${graceDaysLeft()} day${graceDaysLeft() === 1 ? "" : "s"} left before that seat is paused.`
                        : "A seat payment failed — pending seats are now paused until payment is made."}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button onClick={retryCharge} disabled={retryBusy} size="sm" data-testid="firm-retry-charge-btn" className="rounded-sm h-8 text-xs bg-red-600 hover:bg-red-700 text-white">
                        {retryBusy ? "Retrying…" : "Retry charge"}
                      </Button>
                      <Button onClick={addCard} disabled={cardBusy} size="sm" variant="outline" data-testid="grace-update-card-btn" className="rounded-sm h-8 text-xs">
                        Update card
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {billing && !billing.has_customer ? (
              <p className="text-sm text-muted-foreground">Start a Pro subscription to add a card on file. Once you have one, auto-granted seats bill automatically.</p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 p-3 bg-secondary/60 border border-border">
                  <div className="text-sm" data-testid="firm-card-line">
                    {billing?.card
                      ? <span className="capitalize">{billing.card.brand} ending in <span className="font-semibold">{billing.card.last4}</span> · exp {billing.card.exp_month}/{billing.card.exp_year}</span>
                      : <span className="text-muted-foreground">No card on file{billing?.seat_billing_state === "pending_payment" ? " — a seat is pending payment" : ""}</span>}
                  </div>
                  <Button onClick={addCard} disabled={cardBusy} size="sm" variant="outline" data-testid="firm-add-card-btn" className="rounded-sm gap-1 h-8 text-xs shrink-0">
                    <CreditCard size={13} /> {cardBusy ? "Opening…" : billing?.card ? "Update card" : "Add a card"}
                  </Button>
                </div>
                {billing?.latest && (
                  <div className="flex items-center justify-between mt-3 text-sm" data-testid="firm-latest-receipt">
                    <span className="flex items-center gap-1.5 text-muted-foreground"><ReceiptText size={14} /> Most recent charge</span>
                    <span>
                      <span className="font-semibold">${billing.latest.amount.toFixed(2)}</span>
                      <span className="text-muted-foreground text-xs"> · {new Date(billing.latest.date * 1000).toLocaleDateString()} · </span>
                      <Badge variant="outline" className="text-[0.65rem] mr-2">{billing.latest.status}</Badge>
                      {billing.latest.hosted_invoice_url && (
                        <a href={billing.latest.hosted_invoice_url} target="_blank" rel="noopener noreferrer" data-testid="firm-latest-receipt-link" className="text-accent hover:underline text-xs">View receipt</a>
                      )}
                    </span>
                  </div>
                )}
                {billing?.invoices?.length > 1 && (
                  <div className="mt-2">
                    <button onClick={() => setShowAllInvoices((s) => !s)} data-testid="firm-toggle-invoices" className="text-xs text-accent hover:underline">
                      {showAllInvoices ? "Hide" : `Show all ${billing.invoices.length} invoices`}
                    </button>
                    {showAllInvoices && (
                      <div className="border border-border mt-2" data-testid="firm-invoice-list">
                        {billing.invoices.map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between px-3 py-2 border-b border-border/60 text-xs">
                            <span className="text-muted-foreground">{new Date(inv.date * 1000).toLocaleDateString()}</span>
                            <span className="flex items-center gap-2">
                              <span className="font-medium">${inv.amount.toFixed(2)}</span>
                              <Badge variant="outline" className="text-[0.6rem]">{inv.status}</Badge>
                              {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Receipt</a>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Invite paralegals and staff to work under your Pro subscription. Firm members share access to all your case workspaces.
          </p>
          <div className="flex items-center justify-between gap-2 mb-4 p-3 bg-secondary/60 border border-border" data-testid="seat-usage">
            <span className="text-sm"><span className="font-semibold">{firm.seats_used}</span> of <span className="font-semibold">{firm.seat_limit}</span> staff seat{firm.seat_limit === 1 ? "" : "s"} used</span>
            <Button onClick={buySeats} size="sm" variant="outline" data-testid="add-seats-btn" className="rounded-sm gap-1 h-8 text-xs">
              <UserPlus size={13} /> Add a seat (${firm.seat_price}/mo)
            </Button>
          </div>
          <p className="text-[0.7rem] text-muted-foreground mb-4" data-testid="reassign-hint">To reassign a seat when staff leave, remove them below (frees the seat) and invite their replacement.</p>
          {!firm.owner_active && (
            <p className="text-xs text-accent mb-3" data-testid="firm-need-pro">An active Pro subscription is required to add firm seats.</p>
          )}
          <div className="flex gap-2 mb-5">
            <Input data-testid="firm-invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="paralegal@yourfirm.com" className="rounded-sm" onKeyDown={(e) => e.key === "Enter" && invite()} />
            <Button onClick={invite} disabled={busy} data-testid="firm-invite-btn" className="rounded-sm gap-2 shrink-0 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
              <UserPlus size={16} /> Invite
            </Button>
          </div>
        </>
      )}

      {firm.members?.length > 0 && (
        <div data-testid="firm-members">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Users size={13} /> Members</p>
          <div className="border border-border">
            {firm.members.map((m) => (              <div key={m.user_id} className="flex items-center gap-3 px-3 py-2 border-b border-border/60 text-sm" data-testid={`firm-member-${m.user_id}`}>
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{m.name || m.email}</span>
                  <span className="text-muted-foreground text-xs"> · {m.email}</span>
                  <span className="block text-[0.7rem] text-muted-foreground" data-testid={`firm-active-${m.user_id}`}>
                    {m.last_active_at ? `Last active ${new Date(m.last_active_at).toLocaleDateString()}` : "No activity yet"}
                  </span>
                </div>
                <Badge variant="outline" className="rounded-sm text-[0.65rem]">{m.firm_role || "member"}</Badge>
                {firm.is_owner && m.firm_role !== "owner" && (
                  <button onClick={() => removeMember(m.user_id)} data-testid={`firm-remove-${m.user_id}`} title="Remove & free this seat" className="text-muted-foreground hover:text-accent"><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {firm.is_owner && seats.length > 0 && (
        <div className="mt-5" data-testid="firm-seat-ledger">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><ReceiptText size={13} /> Seat ledger</p>
            {seatSummary.billed_seats > 0 && (
              <span className="text-xs text-muted-foreground" data-testid="seat-cost-summary">
                <span className="font-semibold text-foreground">{seatSummary.billed_seats}</span> billed seat{seatSummary.billed_seats === 1 ? "" : "s"} · <span className="font-semibold text-foreground">${seatSummary.monthly_cost.toFixed(2)}</span>/mo
              </span>
            )}
          </div>
          <div className="border border-border">
            <div className="flex items-center gap-3 px-3 py-1.5 bg-secondary/60 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
              <span className="flex-1">Staff</span><span className="w-20">Joined via</span><span className="w-28 text-right">Seat</span>
            </div>
            {seats.map((s) => {
              const b = BILLING_BADGE[s.billing] || BILLING_BADGE.included;
              return (
                <div key={s.user_id} className="flex items-center gap-3 px-3 py-2 border-t border-border/60 text-sm" data-testid={`seat-row-${s.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{s.name || s.email}</span>
                    <span className="block text-[0.7rem] text-muted-foreground truncate">{s.email}</span>
                  </div>
                  <span className="w-20 text-xs text-muted-foreground capitalize">{s.joined_via === "link" ? "Login link" : s.joined_via}</span>
                  <span className="w-28 text-right">
                    <Badge variant="outline" className={`rounded-sm text-[0.65rem] ${b.cls}`} data-testid={`seat-billing-${s.user_id}`}>{b.label}</Badge>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {firm.is_owner && firm.invites?.length > 0 && (
        <div className="mt-4" data-testid="firm-invites">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Pending invites</p>
          <div className="border border-border">
            {firm.invites.map((i) => (
              <div key={i.id} className="flex items-center gap-3 px-3 py-2 border-b border-border/60 text-sm">
                <span className="flex-1 min-w-0 truncate">{i.email}</span>
                <Badge className="rounded-sm bg-amber-500 text-[0.65rem]">pending</Badge>
                <button onClick={() => cancelInvite(i.id)} data-testid={`firm-cancel-${i.id}`} className="text-muted-foreground hover:text-accent"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
